import type { Locale } from "date-fns/locale";
import { da, de, enCA, enGB, enIN, enUS, es, fr, lb, nb, sv } from "date-fns/locale";
import { defined } from "./typeHelper";

const englishLocalizeWithoutOrdinals: Locale["localize"] = {
  ...enUS.localize,
  ordinalNumber: (value) => value.toString(),
};

/**
 * Creates a hybrid locale that uses English month/day names
 * but preserves the date format ordering of the base locale.
 * E.g., German: "31 Dec 2025" instead of "31 Dez. 2025"
 *
 * Remove or update this when the website supports proper localization.
 */
const withEnglishText = (baseLocale: Locale): Locale => ({
  ...baseLocale,
  localize: englishLocalizeWithoutOrdinals,
  formatDistance: enUS.formatDistance,
  formatRelative: enUS.formatRelative,
});

export interface LocaleInfo {
  locale: Locale;
  displayName: string;
  countryCode: string;
}

export const supportedLocales: LocaleInfo[] = [
  { locale: enUS, displayName: "English (United States)", countryCode: "us" },
  { locale: enGB, displayName: "English (United Kingdom)", countryCode: "gb" },
  { locale: enCA, displayName: "English (Canada)", countryCode: "ca" },
  { locale: enIN, displayName: "English (India)", countryCode: "in" },
  { locale: withEnglishText(da), displayName: "Denmark", countryCode: "dk" },
  { locale: withEnglishText(fr), displayName: "France", countryCode: "fr" },
  { locale: withEnglishText(de), displayName: "Germany", countryCode: "de" },
  { locale: withEnglishText(lb), displayName: "Luxembourg", countryCode: "lu" },
  { locale: withEnglishText(nb), displayName: "Norway", countryCode: "no" },
  { locale: withEnglishText(es), displayName: "Spain", countryCode: "es" },
  { locale: withEnglishText(sv), displayName: "Sweden", countryCode: "se" },
];

export const defaultLocaleInfo = defined(supportedLocales[0]);

export const matchSupportedLocale = (localeCode: string): LocaleInfo | undefined => {
  let result = supportedLocales.find(({ locale }) => locale.code.toLowerCase() === localeCode.toLowerCase());
  if (result) {
    return result;
  }

  const langWithoutRegion = localeCode.split("-")[0];
  if (langWithoutRegion && langWithoutRegion !== localeCode) {
    result = supportedLocales.find(({ locale }) => locale.code.toLowerCase() === langWithoutRegion.toLowerCase());
    if (result) {
      return result;
    }
  }

  return undefined;
};

export const resolveSupportedLocale = (localeCode: string): LocaleInfo =>
  matchSupportedLocale(localeCode) ?? defaultLocaleInfo;
